import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { SuppliersService } from '../suppliers.service'
import { SuppliersRepository } from '../suppliers.repository'
import { Supplier } from '../models/supplier.entity'
import { CountriesService, Country } from '../../countries'

interface SupplierDetailState {
  supplier?: Supplier
  country?: Country
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const SUPPLIER_DETAIL_INITIAL_STATE: SupplierDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class SupplierDetailStore {
  constructor(
    private countriesService: CountriesService,
    private suppliersService: SuppliersService,
    private suppliersRepository: SuppliersRepository,
  ) {}

  private state = signal<SupplierDetailState>(SUPPLIER_DETAIL_INITIAL_STATE)

  // Selectors
  readonly supplier = computed(() => this.state().supplier)
  readonly country = computed(() => this.state().country)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isModified = computed(() => this.state().isModified)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(supplierId?: string): Promise<void> {
    if (!supplierId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.suppliersService.read$(supplierId).subscribe({
        next: async (data) => {
          resolve()
          this.setData(
            data,
            this.getCountry(data.countryCode),
            this.state().supplier?._id
              ? this.state().supplier?._id !== data._id
              : false,
          )
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  getCountry(alpha2Code: string): Country | undefined {
    return this.countriesService.readByCode(alpha2Code)
  }

  async save(supplier: Supplier, notify?: boolean): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.suppliersService.upsert$(supplier).subscribe({
        next: (data) => {
          resolve(true)
          this.setData(data, this.getCountry(data.countryCode), false)
          if (notify === true) {
            this.suppliersRepository.notify({ code: 'SUPPLIER_SAVED' })
          }
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }
  // Reducers

  setData(supplier: Supplier, country?: Country, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      supplier,
      country,
      isModified: isModified ?? this.state().isModified,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
